import React from 'react';
import styled from 'astroturf/react';

import type { LoginModalEventDetail } from '../../LoginModalManager';
import type { ArmSpec } from '../armRegistry';
import { memoryRung, recordDismissal, recordOpen } from '../memory';
import type { MemoryRung } from '../memory';
import { loswpWorkId } from '../workContext';

const RETURN_TO_PAPER_COPY =
  'You found this paper again. That usually means it matters. ' +
  'Download it for keeps this time.';
const DISMISSED_ONCE_COPY =
  "Back for this one? One tap and it's yours. Same as before, still free";
const DISMISSED_REPEATEDLY_COPY =
  "No rush. It's here for free whenever you want it.";
const WELCOME_BACK = 'Welcome back';

// Every read path (stamp/slots/copy/expander) resolves before onOpen, so the
// rung is read once per open and shared; keyed on the per-open detail object.
const rungByDetail = new WeakMap<LoginModalEventDetail, MemoryRung>();

function currentWorkId(): number | undefined {
  return loswpWorkId() ?? undefined;
}

function rungFor(detail: LoginModalEventDetail): MemoryRung {
  const cached = rungByDetail.get(detail);
  if (cached) return cached;
  const rung = memoryRung(currentWorkId());
  rungByDetail.set(detail, rung);
  return rung;
}

function isActiveRung(rung: MemoryRung): boolean {
  return (
    rung === 'return_to_paper' ||
    rung === 'dismissed_once' ||
    rung === 'dismissed_repeatedly'
  );
}

const WelcomeBackChip = styled.div`
  position: absolute;
  top: -14px;
  left: var(--spacing-xs-12, 12px);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 var(--spacing-xs-8, 8px);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--primary-brand, #0645b1);
  color: var(--text-white, #ffffff);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 11px;
  font-weight: 500;
  pointer-events: none;
`;

const WelcomeBackLine = styled.div`
  margin-top: var(--spacing-xxs-8, 8px);
  color: var(--primary-brand, #0645b1);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
`;

export const memoryLadderSpec: ArmSpec = {
  stamp: (ctx) => {
    const rung = rungFor(ctx.detail);
    return isActiveRung(rung) ? { memory_state: rung } : undefined;
  },
  slots: (ctx) => {
    if (rungFor(ctx.detail) !== 'return_to_paper') return undefined;
    return ctx.detail.headerVariant === 'yourPaperCard'
      ? { anchorChip: <WelcomeBackChip>{WELCOME_BACK}</WelcomeBackChip> }
      : { underHeader: <WelcomeBackLine>{WELCOME_BACK}</WelcomeBackLine> };
  },
  configuredTextOverride: (ctx) => {
    switch (rungFor(ctx.detail)) {
      case 'return_to_paper':
        return { buttonsPage: { subheader: RETURN_TO_PAPER_COPY } };
      case 'dismissed_once':
        return { buttonsPage: { subheader: DISMISSED_ONCE_COPY } };
      case 'dismissed_repeatedly':
        return { buttonsPage: { subheader: DISMISSED_REPEATEDLY_COPY } };
      default:
        return undefined;
    }
  },
  ctaDefaultExpanded: (ctx) => rungFor(ctx.detail) === 'dismissed_once',
  onOpen: () => recordOpen(currentWorkId()),
  onClose: () => recordDismissal(currentWorkId()),
};
